import React, { useCallback, useEffect, useRef, useState } from "react"
import { Row, Col, CardHeader, CardBody, Spinner, Button, Input, InputGroup, Tooltip } from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { toast, ToastContainer } from "react-toastify"
import { getModernTableStyles } from "../Tables/modernTableStyles"
import { getMyReferralSummary, getMyReferrals } from "../../api/requests/referral-requests"

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const PAGE_SIZE = 20

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const statusMap = {
  PENDING: { label: "Kayıt oldu, paket seçmesi bekleniyor", color: "#F2C96D" },
  QUALIFIED: { label: "Paket seçti, ödül ekleniyor", color: ORANGE },
  REWARDED: { label: "Tamamlandı", color: "#71C30B" },
  CANCELLED: { label: "İptal", color: "#B0B7C3" },
  REJECTED: { label: "Geçersiz", color: "#E07A7A" },
}

const rewardLabel = (status, months) => {
  switch (status) {
    case "APPLIED": return `+${months} ay eklendi`
    case "PENDING": return `${months} ay (birkaç dakika içinde eklenir)`
    case "ON_HOLD": return `${months} ay (aktif abonelik bekleniyor)`
    case "CANCELLED": return "İptal"
    case "REVOKED": return `${months} ay geri alındı (ödeme iade edildi)`
    default: return months > 0 ? `${months} ay` : "-"
  }
}

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "-")

const headline = (mine, theirs) => {
  if (mine > 0 && theirs > 0) return `Arkadaşını getir, ${mine} ay sana ${theirs} ay ona`
  if (mine > 0) return `Arkadaşını getir, ${mine} ay kazan`
  if (theirs > 0) return `Arkadaşını getir, ona ${theirs} ay hediye`
  return "Arkadaşını getir"
}

const rewardStep = ({ referrerRewardMonths: mine, referredRewardMonths: theirs }) => {
  const parts = []
  if (mine > 0) parts.push(`senin aboneliğine ${mine} ay`)
  if (theirs > 0) parts.push(`onun aboneliğine ${theirs} ay`)
  return `Paket seçtiği anda ${parts.join(", ")} otomatik eklenir.`
}

const StatBox =({ label, value, color }) => (
  <div className="text-center px-3">
    <div className="fw-bold" style={{ fontSize: "1.6rem", color: color || NAVY }}>{value}</div>
    <div className="text-muted" style={{ fontSize: ".85rem" }}>{label}</div>
  </div>
)

const ReferralProgram = () => {
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [list, setList] = useState({ content: [], totalElements: 0, totalPages: 0 })
  const [page, setPage] = useState(0)
  const [listLoading, setListLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef(null)

  document.title = "Arkadaşını Getir | Tahsilat360"

  const styles = `
    ${getModernTableStyles("referral-modern")}
    @media (max-width: 576px) {
      .referral-modern .table { font-size: 12px; }
      .referral-modern .table td, .referral-modern .table th { padding: .5rem; }
    }
  `

  useEffect(() => {
    let active = true
    getMyReferralSummary()
      .then((res) => { if (active) setSummary(res.data) })
      .catch((err) => toast.error(err.message, { position: "top-right", autoClose: 3000 }))
      .finally(() => { if (active) setSummaryLoading(false) })
    return () => { active = false }
  }, [])

  const loadList = useCallback(async (targetPage) => {
    setListLoading(true)
    try {
      const res = await getMyReferrals(targetPage, PAGE_SIZE)
      setList(res.data || { content: [], totalElements: 0, totalPages: 0 })
    } catch (err) {
      toast.error(err.message, { position: "top-right", autoClose: 3000 })
    } finally {
      setListLoading(false)
    }
  }, [])

  useEffect(() => { loadList(page) }, [page, loadList])

  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(summary.link)
      setCopied(true)
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error("Link kopyalanamadı, elle seçip kopyalayabilirsiniz", { position: "top-right", autoClose: 3000 })
    }
  }

  const renderStatus = (status) => {
    const s = statusMap[status] || { label: status, color: "#B0B7C3" }
    return (
      <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: s.color, color: "#fff" }}>
        {s.label}
      </span>
    )
  }

  return (
    <div className="page-content referral-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row className="mb-3">
          <Col>
            <TableCard>
              <CardBody className="p-4">
                {summaryLoading ? (
                  <div className="text-center p-4"><Spinner color="primary" /></div>
                ) : !summary ? (
                  <p className="text-muted mb-0">Davet bilgileri yüklenemedi.</p>
                ) : (
                  <>
                    <h4 className="fw-bold mb-2" style={{ color: NAVY }}>
                      {headline(summary.referrerRewardMonths, summary.referredRewardMonths)}
                    </h4>
                    <div className="text-muted mb-3" style={{ fontSize: ".95rem" }}>
                      <div className="fw-semibold text-dark mb-1">Nasıl çalışır?</div>
                      <ol className="mb-1 ps-3">
                        <li>Aşağıdaki davet linkini, Tahsilat360'ı kullanmasını istediğin firmaya gönder.</li>
                        <li>Linke tıklayıp kayıt olsun ve bir paket seçsin (ücretli ya da ücretsiz).</li>
                        <li>{rewardStep(summary)}</li>
                      </ol>
                      <small className="d-block">
                        O firma ücretli bir paket alıp parasını geri alırsa, eklenen süreler ikinizden de geri alınır.
                      </small>
                      <small className="d-block">
                        Linkini ya da kodunu istediğin kadar firmayla paylaşabilirsin; kayıt olup paket seçen her firma
                        için sana ayrı ayrı süre eklenir.
                      </small>
                    </div>

                    {!summary.programActive && (
                      <div className="alert alert-warning py-2">
                        Davet programı şu an aktif değil. Önceki davetleriniz tamamlanmaya devam eder.
                      </div>
                    )}

                    <div className="text-muted mb-1" style={{ fontSize: ".8rem" }}>DAVET LİNKİN</div>
                    <InputGroup className="mb-2" style={{ maxWidth: 640 }}>
                      <Input readOnly value={summary.link} onFocus={(e) => e.target.select()} />
                      <Button
                        id="referralCopyBtn"
                        onClick={copyLink}
                        style={{ backgroundColor: ORANGE, borderColor: ORANGE }}
                        disabled={!summary.programActive}
                      >
                        <i className="bx bx-copy me-1" />Kopyala
                      </Button>
                      <Tooltip isOpen={copied} target="referralCopyBtn" placement="top">
                        Kopyalandı
                      </Tooltip>
                    </InputGroup>
                    <div className="text-muted" style={{ fontSize: ".85rem" }}>
                      Davet kodun: <strong className="text-dark">{summary.code}</strong>
                    </div>

                    <div className="d-flex flex-wrap justify-content-around mt-4 pt-3 border-top">
                      <StatBox label="Toplam davet" value={summary.totalInvited} />
                      <StatBox label="Bekleyen" value={summary.pendingCount} color={ORANGE} />
                      <StatBox label="Başarılı" value={summary.successfulCount} color="#71C30B" />
                      <StatBox label="Kazanılan süre" value={`${summary.totalMonthsEarned} ay`} />
                    </div>

                    {summary.termsText && (
                      <details className="mt-3">
                        <summary className="text-muted" style={{ cursor: "pointer", fontSize: ".85rem" }}>
                          Kampanya koşulları
                        </summary>
                        <p className="text-muted mt-2 mb-0" style={{ whiteSpace: "pre-line", fontSize: ".85rem" }}>
                          {summary.termsText}
                        </p>
                      </details>
                    )}
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>

        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <h5 className="mb-1 fw-bold text-dark">Davetlerim</h5>
                <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                  {list.totalElements > 0 ? `Toplam ${list.totalElements} davet` : "Henüz davet yok"}
                </p>
              </CardHeader>
              <CardBody className="pt-2">
                {listLoading ? (
                  <div className="text-center p-5"><Spinner color="primary" /></div>
                ) : list.content.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-gift display-4 d-block mb-2 text-muted" />
                    <p className="text-muted mb-0">Linkinizi paylaşın, kayıt olan firmalar burada görünür.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Firma</Th>
                            <Th>Davet tarihi</Th>
                            <Th>Durum</Th>
                            <Th>Senin ödülün</Th>
                            <Th>Onun ödülü</Th>
                            <Th>Ödeme tarihi</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {list.content.map((item) => (
                            <Tr key={item.id} className="border-bottom">
                              <Td className="fw-semibold text-dark">{item.referredCompanyName}</Td>
                              <Td>{formatDate(item.createdAt)}</Td>
                              <Td>{renderStatus(item.status)}</Td>
                              <Td>
                                {rewardLabel(item.referrerRewardStatus, item.referrerRewardMonths)}
                              </Td>
                              <Td>{rewardLabel(item.referredRewardStatus, item.referredRewardMonths)}</Td>
                              <Td>{formatDate(item.qualifiedAt)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </div>
                    {list.totalPages > 1 && (
                      <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                        <Button size="sm" className="btn-rounded btn-navy-outline" disabled={page === 0}
                                onClick={() => setPage(page - 1)}>
                          Önceki
                        </Button>
                        <span className="text-muted" style={{ fontSize: ".9rem" }}>
                          {page + 1} / {list.totalPages}
                        </span>
                        <Button size="sm" className="btn-rounded btn-navy-outline" disabled={page + 1 >= list.totalPages}
                                onClick={() => setPage(page + 1)}>
                          Sonraki
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  )
}

export default ReferralProgram
